import { useCallback, useEffect, useState } from 'react';
import {
  FEEDBACK_NOTE_STATUSES,
  type FeedbackNote,
  type FeedbackNoteStatus,
} from '@coffman-tm/shared';
import { api } from '../lib/api';
import { isSameOriginRoute } from '../lib/feedback-page';
import { useAuth } from '../lib/AuthContext';
import { useToast } from '../components/ToastProvider';
import { Container, Dropdown, PageHead } from '../components/design';

/**
 * Brandon's feedback review surface (/admin/feedback). Dense table of every
 * note for the project; rows expand to show the full note + auto-captured
 * context + a response/status editor. Admin-gated (server 403s non-admins;
 * the page also guards on role).
 */
type StatusFilter = FeedbackNoteStatus | 'all';

const STATUS_LABEL: Record<FeedbackNoteStatus, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  shipped: 'Shipped',
  wont_fix: "Won't fix",
};

const STATUS_CHIP: Record<FeedbackNoteStatus, string> = {
  new: 'status-chip s-pending',
  reviewed: 'status-chip s-open',
  shipped: 'status-chip s-approved',
  wont_fix: 'status-chip s-skipped',
};

const STATUS_OPTIONS = FEEDBACK_NOTE_STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }));
const FILTER_OPTIONS = [{ value: 'all', label: 'All' }, ...STATUS_OPTIONS];

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.round(diff / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function FeedbackAdminPage() {
  const { token, me } = useAuth();
  const toast = useToast();
  const [filter, setFilter] = useState<StatusFilter>('new');
  const [notes, setNotes] = useState<FeedbackNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const isAdmin = me?.user.role === 'admin';

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErr(null);
    try {
      const res = await api.feedback.list(token, filter === 'all' ? undefined : filter);
      setNotes(res.notes);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load notes');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!isAdmin) {
    return (
      <Container>
        <PageHead eyebrow="Admin" title="Feedback notes" />
        <p className="muted" style={{ fontSize: 13 }}>This page is for admins only.</p>
      </Container>
    );
  }

  return (
    <Container>
      <PageHead eyebrow="Admin" title="Feedback notes" />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
        <label style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>Status</label>
        <Dropdown
          options={FILTER_OPTIONS}
          value={filter}
          onChange={(v) => setFilter(v as StatusFilter)}
        />
        <span className="muted" style={{ fontSize: 12.5 }}>
          {notes.length} note{notes.length === 1 ? '' : 's'}
        </span>
      </div>

      {loading ? (
        <p className="muted" style={{ fontSize: 13 }}>Loading…</p>
      ) : err ? (
        <p style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</p>
      ) : notes.length === 0 ? (
        <p className="muted" style={{ fontSize: 13 }}>No notes for this filter.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>User</th>
              <th>Page</th>
              <th>Note</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {notes.map((n) => (
              <FeedbackRow
                key={n.id}
                note={n}
                expanded={expandedId === n.id}
                onToggle={() => setExpandedId(expandedId === n.id ? null : n.id)}
                onSaved={async () => {
                  await load();
                }}
                token={token!}
                toast={toast}
              />
            ))}
          </tbody>
        </table>
      )}
    </Container>
  );
}

function FeedbackRow({
  note,
  expanded,
  onToggle,
  onSaved,
  token,
  toast,
}: {
  note: FeedbackNote;
  expanded: boolean;
  onToggle: () => void;
  onSaved: () => Promise<void>;
  token: string;
  toast: ReturnType<typeof useToast>;
}) {
  const [status, setStatus] = useState<FeedbackNoteStatus>(note.status);
  const [response, setResponse] = useState<string>(note.brandon_response ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Re-sync local editor state if the underlying note changes (after a refetch).
  useEffect(() => {
    setStatus(note.status);
    setResponse(note.brandon_response ?? '');
  }, [note.status, note.brandon_response]);

  const context = parseContext(note.page_context_json);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.feedback.patch(token, note.id, {
        status,
        brandon_response: response,
      });
      toast.push({ variant: 'success', title: 'Saved', body: 'Note updated.' });
      await onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr className="clickable" onClick={onToggle} style={{ cursor: 'pointer' }}>
        <td className="muted" style={{ whiteSpace: 'nowrap' }}>{relTime(note.created_at)}</td>
        <td>{note.user_name}</td>
        <td>
          {isSameOriginRoute(note.page_route) ? (
            <a
              href={note.page_route}
              target="_blank"
              rel="noreferrer"
              className="btn-link"
              onClick={(e) => e.stopPropagation()}
            >
              {note.page_label}
            </a>
          ) : (
            // Not a same-origin relative path — show the label, don't make it
            // clickable. Nothing the app records lands here.
            <span>{note.page_label}</span>
          )}
        </td>
        <td>
          {note.note_text.slice(0, 100)}
          {note.note_text.length > 100 ? '…' : ''}
        </td>
        <td>
          <span className={STATUS_CHIP[note.status]}>{STATUS_LABEL[note.status]}</span>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5} style={{ background: 'var(--paper-3)' }}>
            <div style={{ padding: '10px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                {note.note_text}
              </div>

              <div className="muted" style={{ fontSize: 12 }}>
                {note.viewport_width && note.viewport_height
                  ? `Viewport ${note.viewport_width}×${note.viewport_height}`
                  : 'Viewport n/a'}{' '}
                · route <span className="mono">{note.page_route}</span>
              </div>

              {context.length > 0 && (
                <div style={{ fontSize: 12 }}>
                  <span className="muted">Context:</span>{' '}
                  {context.map(([k, v]) => (
                    <span key={k} className="mono" style={{ marginRight: 10 }}>
                      {k}=<span style={{ color: 'var(--ink-2)' }}>{v}</span>
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>Status</span>
                  <Dropdown
                    options={STATUS_OPTIONS}
                    value={status}
                    onChange={(v) => setStatus(v as FeedbackNoteStatus)}
                    disabled={busy}
                  />
                </label>
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)' }}>
                  Response (internal)
                </span>
                <textarea
                  value={response}
                  onChange={(e) => setResponse(e.target.value)}
                  rows={3}
                  style={{ ...selectStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
                  disabled={busy}
                />
              </label>

              {err && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}

              <div>
                <button type="button" className="btn btn-cta" onClick={save} disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function parseContext(json: string | null): [string, string][] {
  if (!json) return [];
  try {
    const obj = JSON.parse(json) as Record<string, unknown>;
    return Object.entries(obj).map(([k, v]) => [k, String(v)]);
  } catch {
    return [];
  }
}

const selectStyle: React.CSSProperties = {
  padding: '6px 9px',
  fontSize: 13,
  border: '1px solid var(--rule-strong, var(--rule))',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'var(--ink)',
};
